import { useEffect, useRef } from "react";
import { useQuery } from "react-query";
import CustomerApiService from "../../../CustomerApiService";
import {
  useEditControl,
  useCollapsedControl,
} from "@cinesys/experience-ui/components";
import { loadLastValues } from "./useLastSchemaValues";
import {
  MetadataUpdateKeyValue,
  MetadataViewerMapping,
  MetadataView,
  MetadataViewerResponse,
  MetadataViewerTimespan,
} from "@cinesys/experience-ui/components";

export interface MetadataSchemaConfig {
  mapping: MetadataViewerMapping;
  views: MetadataView[];
}

const EMPTY_TIMESPAN: MetadataViewerTimespan = {
  field: [],
  group: [],
  schema: undefined,
  id: "generic",
  start: "-INF",
  end: "+INF",
  startLabel: "",
  endLabel: "",
  durationLabel: "",
};

const makeData = (
  schema: string,
  config: MetadataSchemaConfig
): MetadataViewerResponse => ({
  timespans: [{ ...EMPTY_TIMESPAN, schema }],
  views: config.views,
  mapping: config.mapping,
  permissions: {
    write: true,
    create: true,
    remove: false,
    updateGlobal: false,
  },
  hasMetadata: false,
  itemId: "__synthetic__",
});

/**
 * Drives the metadata form for a synthetic (not-yet-created) item: fetches the schema's field
 * mapping/views from `schemaUrl`, wires up `useEditControl` / `useCollapsedControl` against a
 * synthetic `itemId`, and seeds any locally-remembered values for that schema.
 *
 * Shared by the Import flow's schema form (ImportModal) and the Create Placeholder page — both
 * edit metadata for an item that doesn't exist yet and only differ in what happens on submit
 * (import a file vs. create a bare placeholder). Callers should key their component on `schema`
 * (e.g. `<Form key={schema} .../>`) so switching schemas remounts this hook's one-time init
 * effect instead of needing extra reset logic here.
 */
const useMetadataSchemaEditor = (options: {
  schema: string;
  itemId: string;
  schemaUrl: string;
  queryKeyPrefix: string;
  lastValuesStorageKey: string;
  search?: string;
  /** Seeds the form with these values instead of the last-used-per-schema fallback. No caller
   *  passes this today — ImportModal, the Cantemo caller that used it for the Upload page's
   *  "reopen with existing values" case, has no counterpart here. Kept for signature parity with
   *  Cantemo's own hook. */
  initialValues?: MetadataUpdateKeyValue[];
}) => {
  const {
    schema,
    itemId,
    schemaUrl,
    queryKeyPrefix,
    lastValuesStorageKey,
    search,
    initialValues,
  } = options;
  const initialized = useRef(false);

  const configQuery = useQuery({
    queryKey: [queryKeyPrefix, "schema", schema],
    queryFn: () =>
      CustomerApiService.fetch<MetadataSchemaConfig>("GET", schemaUrl),
  });

  const config = configQuery.data;
  const syntheticTimespan = config ? { ...EMPTY_TIMESPAN, schema } : undefined;
  const data = config ? makeData(schema, config) : undefined;

  const editControl = useEditControl({
    type: "item",
    itemId,
    schema,
    timespans: syntheticTimespan ? [syntheticTimespan] : undefined,
    mapping: data?.mapping,
    hasMetadata: false,
    views: data?.views,
    enabled: !!data,
    queryKey: [queryKeyPrefix, itemId, schema],
  });

  const collapsedControl = useCollapsedControl({
    search,
    timespans: syntheticTimespan ? [syntheticTimespan] : undefined,
    mapping: data?.mapping,
  });

  useEffect(() => {
    if (!editControl || !data || initialized.current) {
      return;
    }
    initialized.current = true;
    editControl.setEditingKey("/", true);
    const values =
      initialValues ?? loadLastValues(lastValuesStorageKey, schema);
    if (values.length) {
      editControl.setValues(values);
    }
  }, [editControl, data]);

  return {
    configQuery,
    data,
    syntheticTimespan,
    editControl,
    collapsedControl,
  };
};

export default useMetadataSchemaEditor;
